import {
  Breakpoint,
  CheckboxSize,
  CheckboxType,
  CheckboxVariant,
  ChooseAnswersFieldsGroup,
  ChooseAnswersFieldsGroupAnswerType,
  ChooseAnswersFieldsGroupField,
  FormButton,
  FormButtonTheme,
  Typography,
  TypographyVariant,
  UseState,
} from '@elearning-platform/ui';
import React, { useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';

import { isSameArray } from '../../../../utils';
import { ProgressiveQuizData, ProgressiveQuizQuestion, QuizFeedback } from '../../interfaces';
import { QuizResultScreen } from '../quizResultScreen/quizResultScreen.component';

import { ScreenType } from './progressiveQuiz.enums';
import {
  Answer,
  AnswersWrapper,
  ButtonWrapper,
  ContentWrapper,
  ImageWrapper,
  Img,
  QuestionSupportingText,
  QuestionWrapper,
  StyledCheckboxField,
  StyledFormButton,
  StyledMarkdown,
  Wrapper,
} from './progressiveQuiz.styled';
import { ProgressiveQuizProps } from './progressiveQuiz.types';

export const ProgressiveQuiz: React.FC<ProgressiveQuizProps> = (props: ProgressiveQuizProps): JSX.Element => {
  const { baseUrl, callComplete, data }: ProgressiveQuizProps = props;
  const {
    questions,
    quizImages,
    successHeadline,
    successFinalHeadline,
    successSupportingText,
    successFinalSupportingText,
    failSecondAttemptSupportingText,
    failHeadline,
    failSupportingText,
    isSingleChoice,
  }: ProgressiveQuizData = data;
  const [activeScreen, setActiveScreen]: UseState<ScreenType> = useState<ScreenType>(ScreenType.Question);
  const [isImageShown, setIsImageShown]: UseState<boolean> = useState<boolean>(!!questions[0].image);
  const [selectedAnswers, setSelectedAnswers]: UseState<string[]> = useState<string[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex]: UseState<number> = useState<number>(0);
  const [isQuestionPassed, setIsQuestionPassed]: UseState<boolean> = useState<boolean>(false);
  const [isQuestionSubmitted, setIsQuestionSubmitted]: UseState<boolean> = useState<boolean>(false);
  const [isFinished, setIsFinished]: UseState<boolean> = useState<boolean>(false);
  const { t }: TransProps<never> = useTranslation();
  const isQuestionActive: boolean = useMemo((): boolean => activeScreen === ScreenType.Question, [activeScreen]);
  const [attemptsCount, setAttemptsCount]: UseState<number> = useState<number>(0);
  const isSmallDesktop: boolean = useMediaQuery({
    query: Breakpoint.SmallDesktop,
  });
  const isAttemptsLimitReached: boolean = attemptsCount === 2;

  const isLastQuestion: boolean = currentQuestionIndex >= questions.length - 1;

  const supportingText: string = useMemo((): string => {
    if (isQuestionPassed) {
      return questions[currentQuestionIndex].feedback ?? successSupportingText;
    } else if (isAttemptsLimitReached) {
      return questions[currentQuestionIndex].feedback ?? failSecondAttemptSupportingText;
    } else if (isFinished) {
      return successFinalSupportingText;
    } else {
      return failSupportingText;
    }
  }, [
    isQuestionPassed,
    isAttemptsLimitReached,
    isFinished,
    successSupportingText,
    failSecondAttemptSupportingText,
    successFinalSupportingText,
    failSupportingText,
    currentQuestionIndex,
    questions,
  ]);

  useEffect((): void => {
    setIsImageShown(!!questions[0].image);
  }, [questions]);

  const finalFeedback: QuizFeedback[] = questions.map((feedback: ProgressiveQuizQuestion): QuizFeedback => ({
    question: feedback.correctAnswers.map((answer: string): string => answer).join(', '),
    isCorrect: true,
  }));

  const toggleAnswer: (answer: string) => void = (answer: string): void => {
    setSelectedAnswers((prev: string[]): string[] =>
      prev.includes(answer)
        ? prev.filter((a: string): boolean => a !== answer)
        : [...prev, answer]
    );
  };

  const handleSubmit: () => void = (): void => {
    setIsQuestionSubmitted(true);
    const { correctAnswers }: ProgressiveQuizQuestion = questions[currentQuestionIndex];
    const isCorrect: boolean = isSameArray(selectedAnswers, correctAnswers);

    setIsQuestionPassed(isCorrect);
    setActiveScreen(ScreenType.Result);
    setAttemptsCount(attemptsCount + 1);
    if (isLastQuestion && !successFinalHeadline && (isCorrect || attemptsCount === 1)) {
      callComplete();

      return;
    }
  };

  const handleNextQuestion: () => void = (): void => {
    setAttemptsCount(0);
    if (isLastQuestion) {
      setIsFinished(true);
      setIsQuestionPassed(false);
      callComplete();

      return;
    }
    if (questions[currentQuestionIndex + 1].image) {
      setIsImageShown(true);
    }
    setCurrentQuestionIndex((prev: number): number => prev + 1);
    setIsQuestionSubmitted(false);
    setSelectedAnswers([]);
    setActiveScreen(ScreenType.Question);
  };

  const handleRetry: () => void = (): void => {
    setIsQuestionSubmitted(false);
    setSelectedAnswers([]);
    setActiveScreen(ScreenType.Question);
  };

  return (
    <Wrapper>
      {isQuestionActive ? (
        (questions[currentQuestionIndex].image && isImageShown) ? (
          <ImageWrapper>
            <Img src={`${baseUrl}${questions[currentQuestionIndex].image}`} />
            <StyledFormButton
              label={t('competence.app.scrollLesson.componentsV2.progressiveQuiz.showQuestion')}
              onClick={(): void => setIsImageShown(false)}
              variant={FormButtonTheme.SecondaryBrand}
            />
          </ImageWrapper>
        ) : (
          <ContentWrapper>
            <QuestionWrapper>
              <Typography variant={TypographyVariant.TextXL}>
                <StyledMarkdown
                  text={questions.length === 1
                    ? questions[currentQuestionIndex]?.question
                    : t('competence.app.scrollLesson.componentsV2.progressiveQuiz.question',
                        {
                          currentQuestionIndex: currentQuestionIndex + 1,
                          question: questions[currentQuestionIndex]?.question,
                        }
                    )}
                />
              </Typography>
              <QuestionSupportingText variant={TypographyVariant.TextSM}>
                {t('competence.app.scrollLesson.componentsV2.progressiveQuiz.answers')}
              </QuestionSupportingText>
            </QuestionWrapper>
            <AnswersWrapper>
              {isSingleChoice ? (
                <ChooseAnswersFieldsGroup
                  fields={questions[currentQuestionIndex].answers.map((answer: string): ChooseAnswersFieldsGroupField => ({
                    id: answer,
                    label: answer,
                  }))}
                  onChange={setSelectedAnswers}
                  type={ChooseAnswersFieldsGroupAnswerType.SingleChoice}
                />
              ) : (
                questions[currentQuestionIndex].answers.map((answer: string): JSX.Element => (
                  <Answer key={`${questions[currentQuestionIndex].id}_${answer}`}>
                    <StyledCheckboxField
                      checked={!!selectedAnswers.includes(answer)}
                      label={answer}
                      onChange={(): void => toggleAnswer(answer)}
                      size={CheckboxSize.Medium}
                      type={CheckboxType.Checkbox}
                      variant={CheckboxVariant.SimpleText}
                    />
                  </Answer>
                ))
              )}
            </AnswersWrapper>
          </ContentWrapper>
        )
      ) : (
        <QuizResultScreen
          correctAnswers={questions[currentQuestionIndex].correctAnswers}
          feedback={finalFeedback}
          headline={isQuestionPassed
            ? successHeadline
            : isFinished
              ? successFinalHeadline
              : failHeadline
          }
          image={`${baseUrl}${isQuestionPassed
            ? quizImages.successImage
            : isFinished
              ? quizImages.finalImage
              : quizImages.failImage}
          `}
          isAttemptsLimitReached={isAttemptsLimitReached}
          isFinalResult={isFinished}
          isPassed={isQuestionPassed}
          isSubmitted={isQuestionSubmitted}
          onButtonClick={(isQuestionPassed || isAttemptsLimitReached)
            ? !successFinalHeadline && isLastQuestion
                ? undefined
                : handleNextQuestion
            : handleRetry
          }
          supportingText={supportingText}
        />
      )}
      {!!isQuestionActive && !isImageShown && (
        <ButtonWrapper>
          <FormButton
            disabled={selectedAnswers.length === 0}
            fullWidth={!isSmallDesktop}
            label={t('competence.app.scrollLesson.componentsV2.progressiveQuiz.confirm')}
            onClick={handleSubmit}
            variant={FormButtonTheme.SecondaryBrand}
          />
        </ButtonWrapper>
      )}
    </Wrapper>
  );
};
